import React from "react";
import { Link } from "react-router-dom";
import {
  Clock,
  Handshake,
  CircleDollarSign,
  ScrollText,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionHeader, ComplianceBand } from "@/components/Section";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1642522029691-029b5a432954?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzc4MzQwODA3fDA&ixlib=rb-4.1.0&q=85";

const About = () => {
  return (
    <div data-testid="about-page">
      {/* INTRO */}
      <section className="bg-white">
        <div className="container-tight pt-16 md:pt-24 pb-16 md:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-7">
              <div className="overline reveal">Company</div>
              <h1
                data-testid="about-title"
                className="reveal reveal-delay-1 mt-6 font-display text-4xl sm:text-5xl lg:text-[60px] font-bold tracking-tight text-navy leading-[1.05]"
              >
                About Taonga&nbsp;OÜ
              </h1>
              <div className="reveal reveal-delay-2 mt-8 space-y-5 text-[17px] leading-relaxed text-charcoal max-w-2xl">
                <p>
                  Taonga OÜ is a private limited company registered in Estonia.
                  The company is primarily active in real estate-related
                  activities including property holding, rental operations and
                  cooperation with local real estate professionals and service
                  providers.
                </p>
                <p>
                  The company may participate in selected property-related
                  projects directly or through subsidiaries and professional
                  partnerships. Taonga OÜ may also use established European
                  real estate platforms, including Estateguru and Crowdestate,
                  to evaluate indirect property-related opportunities aligned
                  with its long-term operational approach.
                </p>
                <p>
                  In addition, the company maintains selected long-term
                  corporate shareholdings complementary to its business
                  activities.
                </p>
              </div>
            </div>
            <div className="lg:col-span-5 reveal reveal-delay-2">
              <div className="relative">
                <div className="absolute -inset-2 bg-warm/10 -z-10 -translate-x-3 translate-y-3 rounded-md" />
                <img
                  src={ABOUT_IMG}
                  alt="Professional business meeting"
                  className="w-full h-[420px] object-cover rounded-md border border-border"
                  loading="eager"
                  data-testid="about-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OPERATING PRINCIPLES */}
      <section className="section bg-soft" data-testid="operating-principles-section">
        <div className="container-tight">
          <SectionHeader
            overline="Operating Principles"
            title="A clear and partnership-driven structure"
            testId="operating-principles-header"
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Clock, label: "Long-term operational approach" },
              { icon: Handshake, label: "Professional counterparties" },
              { icon: ScrollText, label: "Transparent business relationships" },
              { icon: ShieldCheck, label: "Conservative operational model" },
              { icon: CircleDollarSign, label: "Own-capital structure" },
            ].map(({ icon: Icon, label }, i) => (
              <div
                key={label}
                data-testid={`op-card-${i}`}
                className="hover-lift bg-white border border-border rounded-md p-7 flex items-start gap-4"
              >
                <Icon className="h-5 w-5 text-warm mt-0.5 shrink-0" strokeWidth={1.5} />
                <div className="font-display text-[16px] font-semibold text-navy leading-snug">
                  {label}
                </div>
              </div>
            ))}
          </div>

          <p
            className="mt-12 max-w-3xl text-[16px] leading-relaxed text-charcoal"
            data-testid="operating-principles-paragraph"
          >
            The company conducts activities using its own corporate funds and
            maintains a conservative and partnership-oriented operating model.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-white" data-testid="about-cta-section">
        <div className="container-tight">
          <div className="border border-border rounded-md p-10 lg:p-14 bg-white flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div>
              <div className="overline">Continue</div>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-navy tracking-tight">
                Explore our real estate activities
              </h3>
              <p className="mt-3 text-charcoal max-w-xl">
                Property holding, rental operations, partnerships and long-term
                property-related projects.
              </p>
            </div>
            <Button
              asChild
              data-testid="about-cta-btn"
              className="bg-navy hover:bg-navy/90 text-white h-11 px-6"
            >
              <Link to="/real-estate-activities">
                Real Estate Activities <ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <ComplianceBand testId="about-compliance-band">
        Taonga OÜ operates with its own corporate capital. The company does not
        manage third-party funds and does not provide regulated financial
        services, brokerage, portfolio management or investment advice.
      </ComplianceBand>
    </div>
  );
};

export default About;
